/** Public verification-link manager — Phase 2A-aud-pub.
 *
 * Mounted inside SessionAuditPage's `AuditDetail`. Lets the operator
 * mint a shareable `/verify/<id>?t=<token>` URL for an external
 * auditor (opposing counsel's expert, insurance adjuster, regulator)
 * to verify the chain WITHOUT a NexBasira account.
 *
 * The plaintext token surfaces ONCE on creation in a "copy this now"
 * banner; after that only the prefix + last-used + use-count + expiry
 * appear in the list. Revoke is one click.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@/lib/api";


type LinkRow = {
  id: string;
  session_id: string;
  token_prefix: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
  use_count: number;
  created_by_email: string | null;
};

type IssuedLink = LinkRow & {
  token: string;
  verify_url: string;
};


export function VerificationLinksCard({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [revealed, setRevealed] = useState<IssuedLink | null>(null);
  const [ttlDays, setTtlDays] = useState(90);

  const list = useQuery({
    queryKey: ["verification-links", sessionId],
    queryFn: async () => {
      const res = await api.get<LinkRow[]>(
        `/v1/sessions/${sessionId}/audit/verification-links`,
      );
      return res.data;
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const res = await api.post<IssuedLink>(
        `/v1/sessions/${sessionId}/audit/verification-links`,
        { ttl_days: ttlDays },
      );
      return res.data;
    },
    onSuccess: (issued) => {
      setRevealed(issued);
      void qc.invalidateQueries({
        queryKey: ["verification-links", sessionId],
      });
    },
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      await api.post(
        `/v1/sessions/${sessionId}/audit/verification-links/${id}/revoke`,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["verification-links", sessionId],
      });
    },
  });

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Older browsers: fall through silently — the textarea is still
      // selectable for manual copy.
    }
  };

  return (
    <section className="card space-y-3" data-testid="verification-links-card">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("ui.verificationLinks.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">
          {t("ui.verificationLinks.cardHint")}
        </p>
      </header>

      {revealed && (
        <div
          className="space-y-2 rounded-md border border-cvp-warn bg-cvp-warn/10 px-3 py-2 text-xs text-amber-100"
          role="alert"
          data-testid="verification-link-reveal"
        >
          <p className="font-semibold">
            {t("ui.verificationLinks.revealTitle")}
          </p>
          <code className="block break-all rounded bg-black/40 px-2 py-1 text-amber-200">
            {revealed.verify_url}
          </code>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => copy(revealed.verify_url)}
            >
              {t("ui.verificationLinks.copyUrl")}
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setRevealed(null)}
            >
              {t("ui.verificationLinks.shared")}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label
            className="label text-xs"
            htmlFor="verif-ttl"
          >
            {t("ui.verificationLinks.ttlLabel")}
          </label>
          <input
            id="verif-ttl"
            type="number"
            className="input w-28"
            min={1}
            max={730}
            value={ttlDays}
            onChange={(e) => setTtlDays(Number(e.target.value) || 90)}
          />
        </div>
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={() => create.mutate()}
          disabled={create.isPending}
          data-testid="verification-link-mint"
        >
          {create.isPending
            ? t("ui.verificationLinks.minting")
            : t("ui.verificationLinks.mint")}
        </button>
      </div>

      <div className="space-y-1 text-sm">
        {list.isLoading && <p className="text-nb-text-2">{t("ui.verificationLinks.loading")}</p>}
        {list.data && list.data.length === 0 && (
          <p className="text-nb-text-2">{t("ui.verificationLinks.noLinks")}</p>
        )}
        <ul className="space-y-1">
          {(list.data ?? []).map((l) => (
            <li
              key={l.id}
              className="rounded-md border border-nb-border bg-slate-900/40 px-3 py-2 text-xs"
            >
              <div className="flex items-center justify-between gap-2">
                <code className="text-nb-text">
                  cvp_av_{l.token_prefix}…
                </code>
                <span
                  className={`rounded px-2 py-0.5 ${
                    l.revoked_at
                      ? "bg-slate-700 text-nb-text-2"
                      : new Date(l.expires_at) < new Date()
                        ? "bg-slate-700 text-nb-text-2"
                        : "bg-emerald-500/15 text-emerald-300"
                  }`}
                >
                  {l.revoked_at
                    ? t("ui.verificationLinks.statusRevoked")
                    : new Date(l.expires_at) < new Date()
                      ? t("ui.verificationLinks.statusExpired")
                      : t("ui.verificationLinks.statusActive")}
                </span>
              </div>
              <div className="mt-0.5 text-nb-text-3">
                {l.created_by_email
                  ? t("ui.verificationLinks.createdByLine", {
                      created: formatDateTime(l.created_at),
                      email: l.created_by_email,
                      expires: formatDateTime(l.expires_at),
                    })
                  : t("ui.verificationLinks.createdLine", {
                      created: formatDateTime(l.created_at),
                      expires: formatDateTime(l.expires_at),
                    })}
              </div>
              <div className="mt-0.5 text-nb-text-3">
                {l.use_count === 0
                  ? t("ui.verificationLinks.notAccessed")
                  : l.last_used_ip
                    ? t("ui.verificationLinks.accessedLineWithIp", {
                        count: l.use_count,
                        when: l.last_used_at && formatDateTime(l.last_used_at),
                        ip: l.last_used_ip,
                      })
                    : t("ui.verificationLinks.accessedLine", {
                        count: l.use_count,
                        when: l.last_used_at && formatDateTime(l.last_used_at),
                      })}
              </div>
              {!l.revoked_at && (
                <button
                  type="button"
                  className="btn-danger mt-2 text-xs"
                  onClick={() => {
                    if (
                      window.confirm(
                        t("ui.verificationLinks.revokeConfirm"),
                      )
                    ) {
                      revoke.mutate(l.id);
                    }
                  }}
                  disabled={revoke.isPending}
                >
                  {t("ui.verificationLinks.revoke")}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
